import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { changeCurrentSiteSuccess, getCurrentSiteId } from "@partikelart/identity/data-access-user";
import { FrontendReport } from "@partikelart/portal/shared-typing";
import * as Sentry from "@sentry/angular";
import { combineLatest, EMPTY, forkJoin, from, iif, Observable, of } from "rxjs";
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap, toArray, withLatestFrom } from "rxjs/operators";
import { DataExportService } from "../data-export.service";
import * as ExportActions from "./export.actions";
import { currentPartsForUserForExport } from "./export.selectors";

@Injectable()
export class ExportEffects {
	private readonly actions$ = inject(Actions);
	private readonly store = inject(Store);
	private readonly exportService = inject(DataExportService);

	readonly loadPartsForCurrentUser$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ExportActions.loadPartsForUser, changeCurrentSiteSuccess),
			withLatestFrom(this.store.select(currentPartsForUserForExport), this.store.select(getCurrentSiteId)),
			filter(([action, items]) => items.length > 0 || action.type == ExportActions.loadPartsForUser.type),
			switchMap(([action, _, siteId]) => {
				return this.exportService.fetchPossiblePartsForSite(siteId).pipe(
					map((parts) =>
						parts.map((part) => {
							return { ...part, selected: false };
						}),
					),
					map((parts) => ExportActions.loadExportsSuccess({ partsForUser: parts })),
				);
			}),
		);
	});

	readonly loadPartsForArrayOfUsers$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ExportActions.loadPartsForArrayOfUsers),
			switchMap((action) => {
				return from(action.siteIds).pipe(
					mergeMap((siteId) => this.exportService.fetchPossiblePartsForSite(siteId)),
					toArray(),
					map((parts) => parts.flat()),
					tap((parts) =>
						Sentry.addBreadcrumb({
							category: "export",
							message: "parts loaded",
							level: "info",
							data: { count: parts.length },
						}),
					),
					map((parts) => ExportActions.loadExportsSuccess({ partsForUser: parts })),
				);
			}),
		);
	});

	readonly waitForSiteChange$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(changeCurrentSiteSuccess),
			map(() => ExportActions.loadPartsForUser()),
		);
	});

	readonly loadReportsForSelectedPart = createEffect(() => {
		return this.actions$.pipe(
			ofType(ExportActions.downloadReportsForPart),
			switchMap((action) => {
				return this.exportService.getReportsForComponent(action.partNumber).pipe(
					map((reports) =>
						ExportActions.downloadReportForPartSuccess({
							reports,
							partNumber: action.partNumber,
						}),
					),
				);
			}),
		);
	});

	readonly loadReportsForPartsSelection$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(ExportActions.getAllReportForSelectedParts),
			withLatestFrom(this.store.select(currentPartsForUserForExport)),
			tap(([action, parts]) =>
				Sentry.addBreadcrumb({
					category: "export",
					message: "parts selected",
					level: "info",
					data: { count: parts.length },
				}),
			),
			switchMap(([action, parts]) => {
				const arrayOfSelectedSpecNumbers: Observable<FrontendReport[]>[] = parts
					.filter((part) => part.selected == true)
					.map((part) => part.artnr)
					.map((partNo) => this.exportService.getReportsForComponent(partNo));
				return combineLatest(arrayOfSelectedSpecNumbers).pipe(
					map((reports) =>
						ExportActions.getAllReportsForSelectedPartsSuccess({
							arrayofReports: reports,
						}),
					),
				);
			}),
		);
	});
}
